import { useState, useEffect, useCallback } from 'react'
import { get } from '../../fetch-instance'
import { useReportsContext } from '../Reports/ReportsContext'
import closeIcon from '@uswds/uswds/img/usa-icons/close.svg'
import '../../assets/feedback/Feedback.scss'
import { REPORT_TYPES, REPORT_TYPE_LABELS } from './FeedbackReportsConstants'

const ALERT_REPORT_TYPE_LABELS = {
  ...REPORT_TYPE_LABELS,
  [REPORT_TYPES.TRIBAL_TANF]: 'TANF',
}

// Local storage key prefix for dismissed alerts
const DISMISSED_KEY_PREFIX = 'feedbackAlertDismissed_'

/**
 * Get the dismissed timestamp for a given report type and year from localStorage
 */
const getDismissedTimestamp = (reportType, year) => {
  return window.localStorage.getItem(
    `${DISMISSED_KEY_PREFIX}${reportType}_${year}`
  )
}

/**
 * Save the dismissed state to localStorage
 */
const saveDismissedState = (reportType, year, reportCreatedAt) => {
  window.localStorage.setItem(
    `${DISMISSED_KEY_PREFIX}${reportType}_${year}`,
    reportCreatedAt
  )
}

/**
 * Alert banner displayed on Data Files pages when feedback reports are available.
 * Only shown to Data Analysts and Regional Staff when reports exist for their STT.
 * Fetches the latest report internally using the `latest=true` query param.
 * Can be dismissed by the user, with state persisted in localStorage per report type and fiscal year.
 */
const FeedbackReportAlert = ({
  stt = null,
  reportType = REPORT_TYPES.TANF_SSP,
}) => {
  const { yearInputValue, quarterInputValue } = useReportsContext()
  const [latestReportDate, setLatestReportDate] = useState(null)
  const [isDismissed, setIsDismissed] = useState(false)

  useEffect(() => {
    const fetchLatestFeedbackReport = async () => {
      if (!yearInputValue || !quarterInputValue) {
        setLatestReportDate(null)
        setIsDismissed(false)
        return
      }

      const params = {
        year: yearInputValue,
        quarter: quarterInputValue,
        latest: 'true',
        report_type: reportType,
      }
      if (stt) {
        params.stt = stt.id
      }

      const { data, ok, error } = await get(
        `${process.env.REACT_APP_BACKEND_URL}/reports/`,
        { params }
      )

      if (ok && data?.results?.length > 0) {
        const report = data.results[0]
        const dismissedTimestamp = getDismissedTimestamp(
          reportType,
          yearInputValue
        )

        // Show banner if not dismissed OR if a new report is available
        if (dismissedTimestamp && dismissedTimestamp === report.created_at) {
          setLatestReportDate(report.created_at)
          setIsDismissed(true)
        } else {
          setLatestReportDate(report.created_at)
          setIsDismissed(false)
        }
      } else if (ok) {
        setLatestReportDate(null)
        setIsDismissed(false)
      } else {
        console.error('Error fetching feedback reports:', error)
        setLatestReportDate(null)
        setIsDismissed(false)
      }
    }

    fetchLatestFeedbackReport()
  }, [yearInputValue, quarterInputValue, stt, reportType])

  const handleDismiss = useCallback(() => {
    if (yearInputValue && latestReportDate) {
      saveDismissedState(reportType, yearInputValue, latestReportDate)
      setIsDismissed(true)
    }
  }, [yearInputValue, latestReportDate, reportType])

  if (!latestReportDate || isDismissed) return null

  // Format date as MM/DD/YYYY
  const formattedDate = new Date(latestReportDate).toLocaleDateString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
  })

  const reportTypeLabel = ALERT_REPORT_TYPE_LABELS[reportType]

  return (
    <div
      className="usa-alert usa-alert--info margin-top-4 margin-bottom-4"
      role="region"
      aria-labelledby="feedback-alert-text"
      tabIndex={-1}
    >
      <div
        className="usa-alert__body"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}
      >
        <p className="usa-alert__text" id="feedback-alert-text">
          {reportTypeLabel} Feedback Reports Available as of {formattedDate}.
          Please{' '}
          <a
            href={`/feedback-reports?type=${reportType}&year=${yearInputValue}${stt ? `&stt=${stt.name}` : ''}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            review the feedback
          </a>{' '}
          and if needed, resubmit complete and accurate data.
        </p>
        <button
          type="button"
          className="usa-modal__close feedback-modal-close-button"
          onClick={handleDismiss}
          aria-label="Dismiss alert"
          style={{ padding: '0' }}
        >
          <img src={closeIcon} alt="X" />
        </button>
      </div>
    </div>
  )
}

export default FeedbackReportAlert
